import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useMentorshipReadOnly } from '@/hooks/useMentorshipReadOnly';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Video, FileText, CheckCircle2, Lock, MessageSquare, Pencil, Crown, UserCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { MentorMultiSelect, type MentorOption } from '@/components/mentorships/MentorMultiSelect';
import { BilledHoursNote } from '@/components/mentorships/BilledHoursNote';
import {
  useMentorshipGroup, useMentorshipProject, useMentorshipSessions, useSaveMentorshipSession,
  useDeleteMentorshipSession, useMentorshipTasks, useSaveMentorshipTask, useDeleteMentorshipTask,
  useMentorshipHours, usePeopleByIds, useCloseMentorshipGroup, useTaskComments, useAddTaskComment,
  useMentorshipExtraWork, useAddExtraWork, useUpdateExtraWork, useDeleteExtraWork,
  useSessionsCurrentMentors, useMentorshipMentorDirectory, billedSessionHours, billedExtraHours,
  SESSION_STATUS_LABEL, TASK_STATUS_LABEL,
  type MentorshipSession, type MentorshipTask, type MentorshipSessionStatus, type MentorshipTaskStatus,
  type MentorshipExtraWorkRow,
} from '@/hooks/useCustomMentorships';

const fmtDate = (d?: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Sin fecha';
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtH = (n: number) => `${Math.round(n * 100) / 100} h`;


const emptySession = {
  name: '', objective: '', scheduled_date: '', start_time: '', duration_minutes: '60',
  modality: 'virtual', meet_link: '', recording_link: '', transcript_text: '', notes: '',
  status: 'draft' as MentorshipSessionStatus, attendees: [] as string[], mentorIds: [] as string[],
};

export default function MentorshipGroupDetailPage() {
  const { projectId, groupId } = useParams();
  const navigate = useNavigate();
  const readOnly = useMentorshipReadOnly(projectId);

  const { data: project } = useMentorshipProject(projectId);
  const { data: group, isLoading } = useMentorshipGroup(groupId);
  const { data: sessions = [] } = useMentorshipSessions(groupId);
  const { data: tasks = [] } = useMentorshipTasks(groupId);
  const { data: hours } = useMentorshipHours(projectId, Number(project?.contracted_hours ?? 0));

  const participantIds = useMemo(
    () => (group?.mentorship_group_participants ?? []).map((p) => p.user_id),
    [group]
  );
  const { data: people = [] } = usePeopleByIds(participantIds);

  // Atribución vigente de cada sesión (para listarla y precargar su edición).
  const sessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);
  const { data: sessionMentors } = useSessionsCurrentMentors(sessionIds);

  // Figuras de Vivetori de la mentoría (líder y apoyos) y resto de mentores de la
  // plataforma: opciones para atribuir sesiones y trabajo fuera de sesión. Los
  // nombres incluyen además a quien tenga horas atribuidas o tareas asignadas
  // aunque hoy no sea mentor ni participante.
  const directory = useMentorshipMentorDirectory(project);
  const lookupIds = useMemo(
    () => Array.from(new Set([
      ...Array.from(sessionMentors?.values() ?? []).flat(),
      ...tasks.map((t) => t.assignee_id).filter((id): id is string => !!id),
    ])),
    [sessionMentors, tasks]
  );
  const { data: lookupPeople } = usePeopleByIds(lookupIds);
  const mentorNameById = useMemo(() => {
    const map = new Map(directory.nameById);
    (lookupPeople ?? []).forEach((p) => map.set(p.id, p.full_name || p.email));
    return map;
  }, [directory.nameById, lookupPeople]);

  const { data: allExtraWork = [] } = useMentorshipExtraWork(projectId);
  const groupExtraWork = useMemo(
    () => allExtraWork.filter((w) => w.group_id === groupId),
    [allExtraWork, groupId]
  );

  const saveSession = useSaveMentorshipSession();
  const deleteSession = useDeleteMentorshipSession();
  const saveTask = useSaveMentorshipTask();
  const deleteTask = useDeleteMentorshipTask();
  const closeGroup = useCloseMentorshipGroup();
  const addExtraWork = useAddExtraWork();
  const updateExtraWork = useUpdateExtraWork();
  const deleteExtraWork = useDeleteExtraWork();

  const [sessionDialog, setSessionDialog] = useState(false);
  const [editingSession, setEditingSession] = useState<MentorshipSession | null>(null);
  const [sForm, setSForm] = useState(emptySession);
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<MentorshipTask | null>(null);
  const [tForm, setTForm] = useState({ title: '', description: '', assignee_id: '', due_date: '', status: 'pending' as MentorshipTaskStatus, session_id: '', evidence_url: '', evidence_text: '' });
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeForm, setCloseForm] = useState({ closing_result: '', closing_technical_approval: '', closing_recommendation: '' });
  const [commentTask, setCommentTask] = useState<MentorshipTask | null>(null);
  const [workForm, setWorkForm] = useState({ work_date: new Date().toISOString().slice(0, 10), hours: '1', description: '' });
  // null = el usuario no tocó la selección: se propone el apoyo del grupo (o el líder).
  const [workMentorIds, setWorkMentorIds] = useState<string[] | null>(null);
  const [editingWork, setEditingWork] = useState<MentorshipExtraWorkRow | null>(null);
  const [workEditForm, setWorkEditForm] = useState({ work_date: '', hours: '', description: '' });
  const [workEditMentorIds, setWorkEditMentorIds] = useState<string[]>([]);

  const isFull = (hours?.usedPct ?? 0) >= 100;
  const pendingTasks = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled');
  // Lo que descuenta de la bolsa: cada registro redondeado hacia arriba a la media hora.
  const groupSessionHours = sessions
    .filter((s) => s.status === 'completed')
    .reduce((acc, s) => acc + billedSessionHours(s.duration_minutes, project?.hours_rounding), 0);
  const groupExtraHours = groupExtraWork.reduce((acc, w) => acc + billedExtraHours(w.hours, project?.hours_rounding), 0);
  const groupExecuted = groupSessionHours + groupExtraHours;


  if (isLoading) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (!group) return <Card><CardContent className="py-12 text-center text-muted-foreground">Grupo no encontrado.</CardContent></Card>;

  // El apoyo del grupo solo cuenta si sigue siendo mentor de la mentoría (apoyo o
  // el propio líder). Si no hay, el grupo lo atiende el mentor líder: es a quien se propone.
  const supportId = group.support_mentor_id && (!project || directory.mentoriaIds.includes(group.support_mentor_id))
    ? group.support_mentor_id
    : null;
  const leadName = directory.leadId ? mentorNameById.get(directory.leadId) ?? 'Mentor' : null;
  const proposedMentorId = supportId ?? directory.leadId;
  const proposed = proposedMentorId ? [proposedMentorId] : [];
  const workMentorSelection = workMentorIds ?? proposed;

  // Opciones para atribuir horas: el apoyo del grupo primero, luego el líder y los
  // demás apoyos de la mentoría; aparte, el resto de mentores de la plataforma.
  const roleOf = (id: string) => (id === directory.leadId ? ('lider' as const) : ('apoyo' as const));
  const mentoriaOptions: MentorOption[] = [
    ...(supportId ? [{ id: supportId, name: mentorNameById.get(supportId) ?? 'Mentor', role: roleOf(supportId), groupSupport: true }] : []),
    ...[directory.leadId, ...directory.supportIds]
      .filter((id): id is string => !!id && id !== supportId)
      .map((id) => ({ id, name: mentorNameById.get(id) ?? 'Mentor', role: roleOf(id) })),
  ];
  const otherOptions: MentorOption[] = directory.otherIds.map((id) => ({ id, name: mentorNameById.get(id) ?? 'Mentor' }));
  const hasMentorOptions = mentoriaOptions.length + otherOptions.length > 0;
  const NO_MENTOR_ERROR = 'Marca al menos un mentor: las horas se atribuyen a quien las dio.';

  // Asistentes = personas del cliente. Los mentores de Vivetori van en "mentores
  // que dieron la sesión", nunca como asistentes.
  const vivetoriIds = [...directory.mentoriaIds, ...directory.otherIds];
  const clientParticipants = (group.mentorship_group_participants ?? []).filter((p) => !vivetoriIds.includes(p.user_id));

  // El formulario se arma al abrir el diálogo (no en un efecto): propone la
  // atribución vigente de la sesión o, si no tiene, el apoyo del grupo (o el líder).
  const openSessionDialog = (s: MentorshipSession | null) => {
    setEditingSession(s);
    setSForm(s
      ? {
          name: s.name,
          objective: s.objective ?? '',
          scheduled_date: s.scheduled_date ?? '',
          start_time: s.start_time?.slice(0, 5) ?? '',
          duration_minutes: String(s.duration_minutes),
          modality: s.modality,
          meet_link: s.meet_link ?? '',
          recording_link: s.recording_link ?? '',
          transcript_text: s.transcript_text ?? '',
          notes: s.notes ?? '',
          status: s.status,
          attendees: Array.isArray(s.attendees) ? s.attendees : [],
          mentorIds: sessionMentors?.get(s.id) ?? proposed,
        }
      : { ...emptySession, mentorIds: proposed });
    setSessionDialog(true);
  };

  const nameOf = (uid: string | null) => {
    if (!uid) return 'Sin responsable';
    const p = people.find((x) => x.id === uid);
    return p?.full_name || p?.email || mentorNameById.get(uid) || 'Mentor';
  };

  const handleSaveSession = async () => {
    if (!sForm.name.trim()) { toast.error('El nombre de la sesión es obligatorio'); return; }
    if (sForm.status === 'completed' && isFull && editingSession?.status !== 'completed') {
      toast.error('La bolsa de horas está agotada. Pide al super admin ampliarla.');
      return;
    }
    if (sForm.status === 'completed' && hasMentorOptions && sForm.mentorIds.length === 0) {
      toast.error(NO_MENTOR_ERROR);
      return;
    }
    try {
      await saveSession.mutateAsync({
        id: editingSession?.id,
        group_id: group.id,
        projectId: project!.id,
        name: sForm.name.trim(),
        objective: sForm.objective,
        scheduled_date: sForm.scheduled_date || null,
        start_time: sForm.start_time || null,
        duration_minutes: Number(sForm.duration_minutes || 60),
        modality: sForm.modality,
        meet_link: sForm.meet_link,
        recording_link: sForm.recording_link,
        transcript_text: sForm.transcript_text,
        notes: sForm.notes,
        attendees: sForm.attendees.filter((id) => !vivetoriIds.includes(id)),
        status: sForm.status,
        mentorIds: sForm.mentorIds,
      } as any);
      toast.success('Sesión guardada');
      setSessionDialog(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo guardar la sesión');
    }
  };

  const handleSaveTask = async () => {
    if (!tForm.title.trim()) { toast.error('El título es obligatorio'); return; }
    try {
      await saveTask.mutateAsync({
        id: editingTask?.id,
        group_id: group.id,
        title: tForm.title.trim(),
        description: tForm.description,
        assignee_id: tForm.assignee_id || null,
        due_date: tForm.due_date || null,
        status: tForm.status,
        session_id: tForm.session_id || null,
        evidence_url: tForm.evidence_url,
        evidence_text: tForm.evidence_text,
      } as any);
      toast.success('Tarea guardada');
      setTaskDialog(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo guardar la tarea');
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(`/mentorias/${projectId}`)}>
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver al proyecto
      </Button>

      {readOnly && (
        <div className="rounded-lg border bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          Vista de solo lectura: puedes consultar el seguimiento de este grupo, pero no modificarlo.
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{group.name}</h1>
          <p className="text-sm text-muted-foreground">
            {project?.name} · {group.group_type || 'Sin tipo'} · {fmtH(groupExecuted)} de {fmtH(Number(group.estimated_hours))} estimadas
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {supportId ? (
              <span className="flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> Apoyo del grupo: {mentorNameById.get(supportId) ?? 'Mentor'}
                {supportId === directory.leadId ? ' (mentor líder)' : ''}
              </span>
            ) : (
              <span
                className={`flex items-center gap-1 ${directory.supportIds.length > 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}
              >
                <AlertCircle className="w-3 h-3" /> Sin apoyo · lo atiende el mentor líder
              </span>
            )}
            <span className="flex items-center gap-1">
              <Crown className="w-3 h-3" /> Mentor líder: {leadName ?? 'Sin asignar'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {fmtH(groupSessionHours)} en sesiones realizadas + {fmtH(groupExtraHours)} fuera de sesión
          </p>

          {group.objective && <p className="text-sm mt-2 max-w-3xl">{group.objective}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={group.status === 'closed' ? 'secondary' : 'default'}>
            {group.status === 'closed' ? 'Cerrado' : 'Activo'}
          </Badge>
          {group.status !== 'closed' && !readOnly && (
            <Button variant="outline" size="sm" onClick={() => setCloseDialog(true)}>Cerrar grupo</Button>
          )}
        </div>
      </div>

      {isFull && (
        <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 rounded-md p-3">
          <Lock className="w-4 h-4" /> La bolsa de horas del proyecto está agotada.
        </div>
      )}

      <Tabs defaultValue="sessions">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="sessions">Sesiones</TabsTrigger>
          <TabsTrigger value="tasks">Tareas ({pendingTasks.length} pendientes)</TabsTrigger>
          <TabsTrigger value="extra">Trabajo fuera de sesión</TabsTrigger>
          <TabsTrigger value="diagnostic">Diagnóstico y participantes</TabsTrigger>
        </TabsList>

        <TabsContent value="extra" className="pt-4 space-y-4">
          <Card className={readOnly ? 'hidden' : undefined}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Registrar trabajo fuera de sesión</CardTitle>
              <CardDescription>Se asocia a este grupo y consume horas de la bolsa del proyecto.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3 items-end">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={workForm.work_date} onChange={(e) => setWorkForm({ ...workForm, work_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Horas</Label>
                <Input type="number" min={0} step="0.25" value={workForm.hours} onChange={(e) => setWorkForm({ ...workForm, hours: e.target.value })} />
              </div>
              <Button
                disabled={isFull || addExtraWork.isPending || group.status === 'closed'}
                onClick={async () => {
                  if (!workForm.description.trim()) { toast.error('Describe el trabajo realizado'); return; }
                  if (hasMentorOptions && workMentorSelection.length === 0) { toast.error(NO_MENTOR_ERROR); return; }
                  try {
                    await addExtraWork.mutateAsync({
                      project_id: project!.id,
                      group_id: group.id,
                      work_date: workForm.work_date,
                      hours: Number(workForm.hours || 0),
                      description: workForm.description.trim(),
                      mentorIds: workMentorSelection,
                    });
                    setWorkForm({ ...workForm, hours: '1', description: '' });
                    setWorkMentorIds(null);
                    toast.success('Trabajo registrado');
                  } catch (e: any) {
                    toast.error(e?.message ?? 'No se pudo registrar');
                  }
                }}
              >
                Registrar
              </Button>
              <BilledHoursNote rounding={project?.hours_rounding} hours={workForm.hours} className="md:col-span-3 -mt-1" />
              <div className="space-y-2 md:col-span-3">
                <Label>Descripción</Label>
                <Textarea rows={2} value={workForm.description} onChange={(e) => setWorkForm({ ...workForm, description: e.target.value })} />
              </div>
              <div className="md:col-span-3">
                <MentorMultiSelect
                  label="Mentores que hicieron el trabajo"
                  mentoriaMentors={mentoriaOptions}
                  otherMentors={otherOptions}
                  value={workMentorSelection}
                  onChange={setWorkMentorIds}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-2">
              {groupExtraWork.length === 0 ? (
                <p className="text-sm text-muted-foreground">Este grupo aún no tiene trabajo fuera de sesión.</p>
              ) : (
                groupExtraWork.map((w) => (
                  <div key={w.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{w.description}</div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{w.group_name ?? 'Sin grupo'}</Badge>
                        <span>{fmtDate(w.work_date)}</span>
                        <span>
                          · {fmtH(Number(w.hours))}
                          {billedExtraHours(w.hours, project?.hours_rounding) !== Number(w.hours) && ` (se cobran ${fmtH(billedExtraHours(w.hours, project?.hours_rounding))})`}
                        </span>
                        {w.author_name && <span>· Registrado por {w.author_name}</span>}
                        <span>· Creado el {fmtDateTime(w.created_at)}</span>
                      </div>
                      {w.mentor_names.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1">
                          {w.mentor_names.map((n, i) => (
                            <Badge key={w.mentor_ids[i]} variant="secondary" className="text-xs">{n}</Badge>
                          ))}
                          {w.mentor_names.length > 1 && <Badge variant="outline" className="text-xs">Compartido</Badge>}
                        </div>
                      )}
                    </div>
                    <div className={readOnly ? 'hidden' : 'flex items-center gap-1'}>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingWork(w);
                          setWorkEditForm({ work_date: w.work_date, hours: String(w.hours), description: w.description ?? '' });
                          setWorkEditMentorIds(w.mentor_ids);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={async () => {
                          await deleteExtraWork.mutateAsync({
                            id: w.id,
                            projectId: project!.id,
                            groupId: w.group_id,
                            hours: Number(w.hours),
                            description: w.description,
                          });
                          toast.success('Registro eliminado');
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Dialog open={!!editingWork} onOpenChange={(o) => { if (!o) setEditingWork(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar trabajo fuera de sesión</DialogTitle>
                <DialogDescription>Los cambios de horas quedan en la bitácora del proyecto.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Fecha</Label>
                    <Input type="date" value={workEditForm.work_date} onChange={(e) => setWorkEditForm({ ...workEditForm, work_date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Horas</Label>
                    <Input type="number" min={0} step="0.25" value={workEditForm.hours} onChange={(e) => setWorkEditForm({ ...workEditForm, hours: e.target.value })} />
                  </div>
                </div>
                <BilledHoursNote rounding={project?.hours_rounding} hours={workEditForm.hours} />
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={workEditForm.description} onChange={(e) => setWorkEditForm({ ...workEditForm, description: e.target.value })} />
                </div>
                <MentorMultiSelect
                  label="Mentores que hicieron el trabajo"
                  mentoriaMentors={mentoriaOptions}
                  otherMentors={otherOptions}
                  value={workEditMentorIds}
                  onChange={setWorkEditMentorIds}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingWork(null)}>Cancelar</Button>
                <Button
                  disabled={updateExtraWork.isPending}
                  onClick={async () => {
                    if (!editingWork) return;
                    if (!workEditForm.description.trim()) { toast.error('Describe el trabajo realizado'); return; }
                    if (hasMentorOptions && workEditMentorIds.length === 0) { toast.error(NO_MENTOR_ERROR); return; }
                    try {
                      await updateExtraWork.mutateAsync({
                        id: editingWork.id,
                        project_id: project!.id,
                        group_id: editingWork.group_id,
                        work_date: workEditForm.work_date,
                        hours: Number(workEditForm.hours || 0),
                        description: workEditForm.description.trim(),
                        previous_hours: Number(editingWork.hours),
                        mentorIds: workEditMentorIds,
                      });
                      toast.success('Registro actualizado');
                      setEditingWork(null);
                    } catch (e: any) {
                      toast.error(e?.message ?? 'No se pudo actualizar');
                    }
                  }}
                >
                  Guardar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>



        <TabsContent value="sessions" className="pt-4 space-y-3">
          <div className={readOnly ? 'hidden' : 'flex justify-end'}>
            <Button size="sm" onClick={() => openSessionDialog(null)} disabled={group.status === 'closed'}>
              <Plus className="w-4 h-4 mr-2" /> Nueva sesión
            </Button>
          </div>
          {sessions.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Sin sesiones planificadas.</CardContent></Card>
          ) : (
            sessions.map((s) => (
              <Card key={s.id}>
                <CardContent className="pt-6 flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="font-medium flex items-center gap-2">
                      {s.name}
                      <Badge variant={s.status === 'completed' ? 'default' : 'outline'}>{SESSION_STATUS_LABEL[s.status]}</Badge>
                      <Badge variant="secondary">{s.modality === 'virtual' ? 'Virtual' : 'Presencial'}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(s.scheduled_date)} {s.start_time?.slice(0, 5) ?? ''} · {s.duration_minutes} min
                      {' '}({fmtH(billedSessionHours(s.duration_minutes, project?.hours_rounding))} de bolsa)
                    </div>
                    {(sessionMentors?.get(s.id) ?? []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1">
                        {(sessionMentors?.get(s.id) ?? []).map((id) => (
                          <Badge key={id} variant="secondary" className="text-xs">{mentorNameById.get(id) ?? 'Mentor'}</Badge>
                        ))}
                        {(sessionMentors?.get(s.id) ?? []).length > 1 && <Badge variant="outline" className="text-xs">Compartida</Badge>}
                      </div>
                    )}
                    {s.objective && <p className="text-sm text-muted-foreground max-w-2xl">{s.objective}</p>}
                    <div className="flex gap-3 text-xs pt-1">
                      {s.meet_link && <a className="text-primary underline" href={s.meet_link} target="_blank" rel="noreferrer"><Video className="w-3 h-3 inline mr-1" />Enlace</a>}
                      {s.recording_link && <a className="text-primary underline" href={s.recording_link} target="_blank" rel="noreferrer">Grabación</a>}
                      {s.transcript_text && <span className="text-muted-foreground"><FileText className="w-3 h-3 inline mr-1" />Transcripción</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openSessionDialog(s)}>{readOnly ? 'Ver detalle' : 'Gestionar'}</Button>
                    {!readOnly && <Button size="icon" variant="ghost" onClick={async () => {
                      if (!confirm('¿Eliminar la sesión?')) return;
                      await deleteSession.mutateAsync({ sessionId: s.id, groupId: group.id, projectId: project!.id });
                      toast.success('Sesión eliminada');
                    }}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="tasks" className="pt-4 space-y-3">
          <div className={readOnly ? 'hidden' : 'flex justify-end'}>
            <Button size="sm" disabled={group.status === 'closed'} onClick={() => {
              setEditingTask(null);
              setTForm({ title: '', description: '', assignee_id: '', due_date: '', status: 'pending', session_id: '', evidence_url: '', evidence_text: '' });
              setTaskDialog(true);
            }}>
              <Plus className="w-4 h-4 mr-2" /> Nueva tarea
            </Button>
          </div>
          {tasks.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Sin tareas registradas.</CardContent></Card>
          ) : (
            tasks.map((t) => (
              <Card key={t.id}>
                <CardContent className="pt-6 flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="font-medium flex items-center gap-2">
                      {t.title}
                      <Badge variant={t.status === 'done' ? 'default' : 'outline'}>{TASK_STATUS_LABEL[t.status]}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {nameOf(t.assignee_id)} · vence {fmtDate(t.due_date)}
                      {t.session_id && ` · desde ${sessions.find((s) => s.id === t.session_id)?.name ?? 'una sesión'}`}
                    </div>
                    {t.description && <p className="text-sm text-muted-foreground max-w-2xl">{t.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    {t.status !== 'done' && !readOnly && (
                      <Button size="sm" variant="outline" onClick={async () => {
                        await saveTask.mutateAsync({ id: t.id, group_id: group.id, title: t.title, description: t.description, assignee_id: t.assignee_id, due_date: t.due_date, session_id: t.session_id, evidence_url: t.evidence_url, evidence_text: t.evidence_text, status: 'done' } as any);
                        toast.success('Tarea completada');
                      }}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Completar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setCommentTask(t)}>
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    {!readOnly && <>
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditingTask(t);
                      setTForm({
                        title: t.title, description: t.description ?? '', assignee_id: t.assignee_id ?? '',
                        due_date: t.due_date ?? '', status: t.status, session_id: t.session_id ?? '',
                        evidence_url: t.evidence_url ?? '', evidence_text: t.evidence_text ?? '',
                      });
                      setTaskDialog(true);
                    }}>Editar</Button>
                    <Button size="icon" variant="ghost" onClick={async () => {
                      if (!confirm('¿Eliminar la tarea?')) return;
                      await deleteTask.mutateAsync({ id: t.id, groupId: group.id });
                    }}><Trash2 className="w-4 h-4" /></Button>
                    </>}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="diagnostic" className="pt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Diagnóstico de entrada</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Field label="Situación actual" value={group.diag_current_situation} />
              <Field label="Brecha" value={group.diag_gap} />
              <Field label="Resultado esperado" value={group.diag_expected_result} />
              <Field label="Indicador" value={group.diag_indicator} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Participantes</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {(group.mentorship_group_participants ?? []).length === 0 && <p className="text-muted-foreground">Sin participantes.</p>}
              {(group.mentorship_group_participants ?? []).map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-md border p-2">
                  <span>{nameOf(p.user_id)}</span>
                  {p.is_leader && <Badge title="Responsable del proyecto por parte del cliente">Líder del proyecto</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
          {group.status === 'closed' && (
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-base">Cierre del grupo</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Field label="Resultado" value={group.closing_result} />
                <Field label="Visto bueno técnico" value={group.closing_technical_approval} />
                <Field label="Recomendación" value={group.closing_recommendation} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Sesión */}
      <Dialog open={sessionDialog} onOpenChange={setSessionDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSession ? 'Gestionar sesión' : 'Nueva sesión'}</DialogTitle>
            <DialogDescription>Las horas se descuentan al marcar la sesión como realizada.</DialogDescription>
          </DialogHeader>
          <fieldset disabled={readOnly} className="contents">

          {pendingTasks.length > 0 && (
            <div className="rounded-md border p-3 text-sm">
              <div className="font-medium mb-1">Tareas pendientes del grupo</div>
              <ul className="list-disc pl-5 text-muted-foreground">
                {pendingTasks.map((t) => <li key={t.id}>{t.title} — vence {fmtDate(t.due_date)}</li>)}
              </ul>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Nombre *</Label>
              <Input value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Objetivo</Label>
              <Textarea rows={2} value={sForm.objective} onChange={(e) => setSForm({ ...sForm, objective: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input type="date" value={sForm.scheduled_date} onChange={(e) => setSForm({ ...sForm, scheduled_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Hora de inicio</Label>
              <Input type="time" value={sForm.start_time} onChange={(e) => setSForm({ ...sForm, start_time: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Duración (minutos)</Label>
              <Input type="number" min={15} step={15} value={sForm.duration_minutes} onChange={(e) => setSForm({ ...sForm, duration_minutes: e.target.value })} />
              <BilledHoursNote rounding={project?.hours_rounding} minutes={sForm.duration_minutes} />
            </div>
            <div className="space-y-2">
              <Label>Modalidad</Label>
              <Select value={sForm.modality} onValueChange={(v) => setSForm({ ...sForm, modality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="virtual">Virtual</SelectItem>
                  <SelectItem value="presencial">Presencial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Enlace de Meet</Label>
              <Input value={sForm.meet_link} onChange={(e) => setSForm({ ...sForm, meet_link: e.target.value })} placeholder="https://meet.google.com/..." />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={sForm.status} onValueChange={(v) => setSForm({ ...sForm, status: v as MentorshipSessionStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(['draft', 'planned', 'completed', 'cancelled'] as MentorshipSessionStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{SESSION_STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Enlace de la grabación</Label>
              <Input value={sForm.recording_link} onChange={(e) => setSForm({ ...sForm, recording_link: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Participantes que asistieron</Label>
              <div className="rounded-md border p-3 space-y-2">
                {clientParticipants.length === 0 && (
                  <p className="text-sm text-muted-foreground">El grupo no tiene participantes.</p>
                )}
                {clientParticipants.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={sForm.attendees.includes(p.user_id)}
                      onCheckedChange={(c) =>
                        setSForm({
                          ...sForm,
                          attendees: c
                            ? [...sForm.attendees, p.user_id]
                            : sForm.attendees.filter((id) => id !== p.user_id),
                        })
                      }
                    />
                    {nameOf(p.user_id)}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <MentorMultiSelect
                label="Mentores que dieron la sesión"
                mentoriaMentors={mentoriaOptions}
                otherMentors={otherOptions}
                value={sForm.mentorIds}
                onChange={(ids) => setSForm((prev) => ({ ...prev, mentorIds: ids }))}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Transcripción (pega el texto o sube un archivo .txt / .md)</Label>
              <Input
                type="file"
                accept=".txt,.md,.vtt,.srt,text/plain"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const text = await file.text();
                  setSForm((prev) => ({ ...prev, transcript_text: text }));
                  toast.success('Transcripción cargada');
                }}
              />
              <Textarea rows={5} value={sForm.transcript_text} onChange={(e) => setSForm({ ...sForm, transcript_text: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notas y acuerdos</Label>
              <Textarea rows={4} value={sForm.notes} onChange={(e) => setSForm({ ...sForm, notes: e.target.value })} />
            </div>
          </div>
          </fieldset>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionDialog(false)}>{readOnly ? 'Cerrar' : 'Cancelar'}</Button>
            {!readOnly && <Button onClick={handleSaveSession} disabled={saveSession.isPending}>Guardar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tarea */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Título *</Label>
              <Input value={tForm.title} onChange={(e) => setTForm({ ...tForm, title: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descripción</Label>
              <Textarea rows={3} value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Responsable</Label>
              <Select value={tForm.assignee_id} onValueChange={(v) => setTForm({ ...tForm, assignee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {mentoriaOptions.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Mentores</SelectLabel>
                      {mentoriaOptions.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {clientParticipants.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Participantes</SelectLabel>
                      {clientParticipants.map((p) => (
                        <SelectItem key={p.user_id} value={p.user_id}>{nameOf(p.user_id)}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha límite</Label>
              <Input type="date" value={tForm.due_date} onChange={(e) => setTForm({ ...tForm, due_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Sesión de origen</Label>
              <Select value={tForm.session_id} onValueChange={(v) => setTForm({ ...tForm, session_id: v })}>
                <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                <SelectContent>
                  {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={tForm.status} onValueChange={(v) => setTForm({ ...tForm, status: v as MentorshipTaskStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(['pending', 'in_progress', 'done', 'cancelled'] as MentorshipTaskStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{TASK_STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Evidencia (enlace)</Label>
              <Input value={tForm.evidence_url} onChange={(e) => setTForm({ ...tForm, evidence_url: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Evidencia (texto)</Label>
              <Textarea rows={3} value={tForm.evidence_text} onChange={(e) => setTForm({ ...tForm, evidence_text: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancelar</Button>
            {!readOnly && <Button onClick={handleSaveTask} disabled={saveTask.isPending}>Guardar</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cierre de grupo */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cerrar grupo</DialogTitle>
            <DialogDescription>Registra el resultado final del acompañamiento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Resultado</Label>
              <Textarea rows={3} value={closeForm.closing_result} onChange={(e) => setCloseForm({ ...closeForm, closing_result: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Visto bueno técnico</Label>
              <Textarea rows={2} value={closeForm.closing_technical_approval} onChange={(e) => setCloseForm({ ...closeForm, closing_technical_approval: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Recomendación</Label>
              <Textarea rows={3} value={closeForm.closing_recommendation} onChange={(e) => setCloseForm({ ...closeForm, closing_recommendation: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)}>Cancelar</Button>
            <Button onClick={async () => {
              await closeGroup.mutateAsync({ groupId: group.id, projectId: project!.id, ...closeForm });
              setCloseDialog(false);
              toast.success('Grupo cerrado');
            }}>Cerrar grupo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskCommentsDialog readOnly={readOnly} task={commentTask} onClose={() => setCommentTask(null)} />
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function TaskCommentsDialog({ task, onClose, readOnly = false }: { task: MentorshipTask | null; onClose: () => void; readOnly?: boolean }) {
  const { data: comments = [] } = useTaskComments(task?.id);
  const addComment = useAddTaskComment();
  const [body, setBody] = useState('');

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Comentarios</DialogTitle>
          <DialogDescription>{task?.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {comments.length === 0 && <p className="text-sm text-muted-foreground">Sin comentarios.</p>}
          {comments.map((c: any) => (
            <div key={c.id} className="rounded-md border p-2 text-sm">
              <div>{c.body}</div>
              <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString('es-CO')}</div>
            </div>
          ))}
        </div>
        <div className={readOnly ? 'hidden' : 'flex gap-2'}>
          <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Escribe un comentario…" />
          <Button
            onClick={async () => {
              if (!body.trim() || !task) return;
              await addComment.mutateAsync({ taskId: task.id, body: body.trim() });
              setBody('');
            }}
          >
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
